import type { WidgetGridLayout } from '@trinity/chat';

export function getBlockById(instance: WidgetGridLayout, blockId: string) {
  return instance.blocks.find((block) => block.id === blockId) || null;
}

export function getSectionById(instance: WidgetGridLayout, blockId: string, sectionId: string) {
  const block = getBlockById(instance, blockId);
  if (!block) return null;
  return block.sections.find((section) => section.id === sectionId) || null;
}

/**
 * Returns the display title for the block that contains the given widget ID.
 * Prefers the explicit block title; falls back to the tag name if set.
 */
export function getBlockTitleForWidget(instance: WidgetGridLayout, widgetId: number): string | undefined {
  for (const block of instance.blocks) {
    if (block.widgetId === widgetId) {
      return block.title ?? block.tag?.tagName;
    }
    if (block.sections) {
      const found = blockSectionsContainWidget(block.sections, widgetId);
      if (found) return block.title ?? block.tag?.tagName;
    }
  }
  return undefined;
}

function blockSectionsContainWidget(sections: WidgetGridLayout['blocks'][number]['sections'], widgetId: number): boolean {
  if (!sections) return false;
  for (const section of sections) {
    if (sectionContainsWidget(section, widgetId)) return true;
  }
  return false;
}

function sectionContainsWidget(section: WidgetGridLayout['blocks'][number]['sections'][number], widgetId: number): boolean {
  for (const item of section.contentItems) {
    if (item.type === 'widget' && item.widgetId === widgetId) return true;
  }
  return false;
}

/**
 * Returns all widget IDs in the grid in left-to-right, top-to-bottom reading order.
 * For block level widgets (block.widgetId), the widgetId is included directly.
 * For section-based blocks, contentItems are traversed left-to-right within each section.
 */
export function getOrderedWidgetIds(instance: WidgetGridLayout): number[] {
  const ids: number[] = [];

  for (const block of instance.blocks) {
    if (block.widgetId !== undefined) {
      ids.push(block.widgetId);
    } else if (block.sections) {
      collectWidgetIdsFromSections(block.sections, ids);
    }
  }

  return ids;
}

function collectWidgetIdsFromSections(sections: WidgetGridLayout['blocks'][number]['sections'], ids: number[]): void {
  for (const section of sections) {
    for (const item of section.contentItems) {
      if (item.type === 'widget') {
        ids.push(item.widgetId);
      }
    }
  }
}
